import {
  Button,
  ButtonGroup,
  Listbox,
  ListboxItem,
  Modal,
  ModalBody,
  ModalContent,
  Popover,
  PopoverContent,
  PopoverTrigger, Tooltip,
  useDisclosure
} from "@heroui/react";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';
import toast from 'react-hot-toast';
import {useNavigate} from 'react-router-dom';
import _ from 'lodash';
import React, {useState, useEffect} from 'react';
import { motion, AnimatePresence } from "framer-motion";
import PageLoader from './PageLoader.jsx';
import {generateWebVTT} from '../helpers/AppHelper.jsx';

export default function ExportVideo({data, originalData, onUpdate, ...props}) {
    const {isOpen, onOpen, onOpenChange, onClose} = useDisclosure();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [currentFilePath, setCurrentFilePath] = useState('')
    const {isOpen: exportCreditUseModalIsOpen, onOpenChange: exportCreditUseModalOnOpenChange} = useDisclosure()
    const {isOpen: exportLimitUseModalIsOpen, onOpenChange: exportLimitUseModalOnOpenChange} = useDisclosure()
    const [showWarning, setShowWarning] = useState(false)
    const [showHint, setShowHint] = useState(false);

    useEffect(() => {
        const hasSeenHint = localStorage.getItem('has_seen_export_hint_v1');

        if (!hasSeenHint) {
            setShowHint(true);
            const timer = setTimeout(() => {
                setShowHint(false);
                localStorage.setItem('has_seen_export_hint_v1', 'true');
            }, 4000);

            return () => clearTimeout(timer);
        }
    }, []);

    const exportVideo = useMutation({
        mutationFn: () => repositories.project.exportVideo(data),
        onMutate: () => {
            onUpdate.mutate()
            onClose()
        },
        onSuccess: async (response) => {
            toast.success(response?.message || 'Video export process started in the background')
            if (exportCreditUseModalIsOpen) {
                exportCreditUseModalOnOpenChange()
            }

            if (exportLimitUseModalIsOpen) {
                exportLimitUseModalOnOpenChange()
            }

            await queryClient.invalidateQueries({
                queryKey: ['get-projects']
            })
        },
        onError: (error) => {
            toast.error(error?.response?.data?.detail || 'Something went wrong')
            onClose();
        }
    })

    const exportFile = async (filePath, targetExtension = 'mp4') => {
      if (!filePath) return;

      try {
        setCurrentFilePath(filePath);

        const baseName = data?.project_name
          ? data.project_name.replace(/\.[^/.]+$/, "")
          : 'exported-file';

        const response = await fetch(filePath);
        if (!response.ok) throw new Error('Network response was not ok');

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${baseName}.${targetExtension}`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Error downloading file:', err);
        toast.error("Failed to download file. Please try again.");
      } finally {
        setCurrentFilePath('');
      }
    };

    const iconClasses = "text-xl text-default-500 pointer-events-none flex-shrink-0 bg-slate-100 text-slate-700 rounded-full w-8 h-8 flex justify-center items-center";

    return (
      <>
          {!_.isEmpty(currentFilePath) && <div className="absolute left-0 top-0 w-full h-full bg-white/70 z-50">
              <PageLoader/>
          </div>}

          <div className="relative inline-flex items-center">
              <ButtonGroup variant="flat">
                  <Popover
                    classNames={{
                        content: "min-w-[200px] max-w-[350px]",
                    }}
                    isOpen={isOpen}
                    shouldBlockScroll={false}
                    shouldCloseOnScroll={false}
                    onOpenChange={onOpenChange}
                    placement="bottom-end"
                    showArrow={true}
                  >
                      <Tooltip content="Export your project.">
                        <Button onPress={() => {
                            setShowHint(false);
                            if (data?.current_export_count >= data?.export_limit) {
                                exportCreditUseModalOnOpenChange()
                            } else {
                                exportLimitUseModalOnOpenChange()
                            }
                        }} variant="bordered" color="" isIconOnly fullWidth
                                className="py-5 px-12 text-sm border-divider font-sans border-r-0">
                            <i className="hgi-stroke hgi-folder-export mr-2"></i>
                            export
                        </Button>
                      </Tooltip>
                      <PopoverContent>
                          <div
                            className="w-full flex justify-between items-center border-b border-default-100 px-1 mb-3">
                              <h3 className="text-left w-full font-medium">Are you ready for export the video?</h3>
                              <Button
                                variant="light"
                                color="danger"
                                isIconOnly radius="lg"
                                onPress={onClose}
                                className="rounded-xl"
                              >
                                  <i className="hgi-stroke hgi-cancel-01"></i>
                              </Button>
                          </div>
                          <div className="grid grid-cols-1 gap-3 w-full overflow-y-auto scrollbar-hide py-4 px-3">
                              <h2>Your rendering is complete. Check your registered email for the details. If it failed,
                                  the email will have more information.</h2>

                              <div className="mt-3 text-center">
                                  <Button
                                    variant="solid"
                                    color="primary" className="font-sans"
                                    isLoading={exportVideo.isPending}
                                    onPress={() => {
                                        if (data?.current_export_count >= data?.export_limit) {
                                            exportCreditUseModalOnOpenChange()
                                        } else {
                                            exportLimitUseModalOnOpenChange()
                                        }
                                    }}
                                  >
                                      Export now
                                  </Button>
                              </div>
                          </div>
                      </PopoverContent>
                  </Popover>

                  <Popover
                    classNames={{
                        content: "min-w-[200px] max-w-[350px] text-left justify-start p-0",
                    }}
                    shouldBlockScroll={false}
                    shouldCloseOnScroll={false}
                    placement="bottom-end"
                    showArrow={true}
                  >
                      <PopoverTrigger>
                          <Button variant="bordered" color="" isIconOnly fullWidth
                                  className="py-5 px-5 text-sm border-divider font-sans">
                              <i className="hgi-stroke hgi-file-download"></i>
                          </Button>
                      </PopoverTrigger>
                      <PopoverContent>
                          <Listbox
                            className="p-0 gap-0 divide-y divide-default-300/50 dark:divide-default-100/80 bg-content1 max-w-[300px] overflow-visible rounded-medium"
                            itemClasses={{
                                base: "px-3 first:rounded-t-medium last:rounded-b-medium rounded-none gap-3 h-12 data-[hover=true]:bg-default-100/80 first:pt-3 last:pb-3",
                            }}
                            shouldFocusWrap
                            aria-label="Listbox menu with descriptions" variant="flat">
                              <ListboxItem
                                key="original"
                                isDisabled={_.isEmpty(data?.s3_video_link)}
                                onPress={() => exportFile(data?.s3_video_link)}
                                startContent={<div className={iconClasses}><i className="hgi-stroke hgi-download-05"/>
                                </div>}
                              >
                                  Download original video
                              </ListboxItem>
                              <ListboxItem
                                key="original_audio"
                                isDisabled={_.isEmpty(data?.s3_original_audio_link)}
                                onPress={() => exportFile(data?.s3_original_audio_link, 'mp3')}
                                startContent={<div className={iconClasses}><i className="hgi-stroke hgi-music-note-04"/>
                                </div>}
                              >
                                  Download original Audio
                              </ListboxItem>
                              <ListboxItem
                                key="original_srt"
                                onPress={async () => {
                                    const srtJSON = await generateWebVTT(data?.text_srt_json)
                                    await exportFile(srtJSON, 'srt')
                                }}
                                startContent={<div className={iconClasses}><i
                                  className="hgi-stroke hgi-closed-caption"/></div>}
                              >
                                  Download original SRT
                              </ListboxItem>
                            { _.isEmpty(data?.export_file_link) || _.size(data?.export_file_link) <= 9 && !data?.is_export ?
                              <ListboxItem
                                key="translated"
                                className="!opacity-50"
                              >
                                <Tooltip className="!w-full !h-full"  color="primary" content="Please Export Your Project First!">
                                  <div className="flex items-center justify-start gap-2 w-full h-full">
                                    <div className={iconClasses}><i className="hgi-stroke hgi-translation"/></div>
                                    <p>Download translated video</p>
                                  </div>
                                </Tooltip>
                              </ListboxItem>
                              :
                              <ListboxItem
                                key="translated"
                                isDisabled={_.isEmpty(data?.export_file_link) || _.size(data?.export_file_link) <= 9 && !data?.is_export}
                                onPress={() => exportFile(data?.export_file_link, 'zip')}
                                startContent={<div className={iconClasses}><i className="hgi-stroke hgi-translation"/>
                                </div>}
                              >
                                Download translated video
                              </ListboxItem>
                            }
                              <ListboxItem
                                key="translated_audio"
                                isDisabled={_.isEmpty(data?.translated_video_link) || _.size(data?.translated_video_link) <= 9 && !data?.is_export}
                                onPress={() => exportFile(data?.translated_video_link, "mp3")}
                                startContent={<div className={iconClasses}><i className="hgi-stroke hgi-translation"/>
                                </div>}
                              >
                                  Download translated audio
                              </ListboxItem>
                              <ListboxItem
                                key="translated_srt"
                                onPress={async () => {
                                    if (data?.text_srt_json?.some(item => item?.has_timeline_changed)) return setShowWarning(true)
                                    const srtJSON = await generateWebVTT(originalData?.text_srt_json, false)
                                    await exportFile(srtJSON, 'srt')
                                }}
                                startContent={<div className={iconClasses}><i className="hgi-stroke hgi-open-caption"/>
                                </div>}
                              >
                                  Download translated SRT
                              </ListboxItem>
                          </Listbox>
                      </PopoverContent>
                  </Popover>
              </ButtonGroup>

              <AnimatePresence>
                  {showHint && (
                    <motion.div
                      initial={{ opacity: 0, y: 0 }}
                      animate={{
                          opacity: 1,
                          y: [10, 20, 10],
                          transition: {
                              y: {
                                  repeat: Infinity,
                                  duration: 1.5,
                                  ease: "easeInOut"
                              }
                          }
                      }}
                      exit={{ opacity: 0 }}
                      className="absolute top-full right-0 mt-2 z-50 pointer-events-none"
                    >
                        <div className="bg-blue-600 text-white text-[12px] px-3 py-2 rounded-lg shadow-lg whitespace-nowrap relative z-[100]">
                            <div className="absolute -top-1 right-14 w-2 h-2 bg-blue-600 rotate-45"></div>
                            🚀 Clicked Here to Export your project!
                        </div>
                    </motion.div>
                  )}
              </AnimatePresence>
          </div>


          {/* Export Limit Warning — caution */}
          <Modal
            isOpen={exportCreditUseModalIsOpen}
            onOpenChange={exportCreditUseModalOnOpenChange}
            size="lg"
            hideCloseButton
            backdrop="blur"
            classNames={{
                base: "overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl",
                body: "p-0",
            }}>
              <ModalContent>
                  {(onClose) => (
                    <ModalBody>
                        <div className="relative overflow-hidden bg-gradient-to-br from-[#f59e0b] via-[#f5a623] to-[#fbbf24] px-8 pt-9 pb-12">
                            <div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
                            <div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
                            <div className="relative flex flex-col items-center text-center">
                                <div className="relative grid h-16 w-16 place-items-center">
                                    <span className="absolute inset-0 rounded-2xl bg-white/30 animate-ping" />
                                    <span className="relative grid h-16 w-16 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40">
                                        <i className="hgi-stroke hgi-download-04 text-3xl text-white" />
                                    </span>
                                </div>
                                <h2 className="mt-4 text-xl font-semibold text-white">Export limit reached</h2>
                                <p className="mt-1 max-w-sm text-sm font-light text-white/85">
                                    Your free exports are all used up.
                                </p>
                            </div>
                        </div>

                        <div className="px-6 pt-5">
                            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                                <i className="hgi-stroke hgi-coins-01 mt-0.5 text-lg text-amber-500" />
                                <p className="text-sm font-light leading-relaxed text-amber-700">
                                    You have used all of your Export limit. Now, <strong className="font-semibold">{data?.export_credit} credits</strong> will be
                                    deducted from your main credit balance. Would you like to continue?
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 px-6 py-5">
                            <Button
                              variant="bordered"
                              className="h-11 rounded-xl border-1 border-[#00000014] bg-[#c8ceda4d] px-6 text-sm font-medium text-slate-700 hover:border-slate-700"
                              onPress={exportCreditUseModalOnOpenChange}>Cancel</Button>
                            <Button
                              isLoading={exportVideo.isPending}
                              className="h-11 rounded-xl bg-[#7659fa] px-6 text-sm font-medium text-white shadow-lg shadow-[#7659fa40] transition hover:opacity-95"
                              onPress={() => {
                                  exportVideo.mutate()
                              }}>Confirm</Button>
                        </div>
                    </ModalBody>
                  )}
              </ModalContent>
          </Modal>


          {/* Ready to export — primary, with usage stats */}
          <Modal
            isOpen={exportLimitUseModalIsOpen}
            onOpenChange={exportLimitUseModalOnOpenChange}
            size="lg"
            hideCloseButton
            backdrop="blur"
            classNames={{
                base: "overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl",
                body: "p-0",
            }}>
              <ModalContent>
                  {(onClose) => (
                    <ModalBody>
                        <div className="relative overflow-hidden bg-gradient-to-br from-[#7659fa] via-[#8a6cff] to-[#a78bfa] px-8 pt-9 pb-12">
                            <div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
                            <div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
                            <div className="relative flex flex-col items-center text-center">
                                <div className="grid h-16 w-16 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40">
                                    <i className="hgi-stroke hgi-video-replay text-3xl text-white" />
                                </div>
                                <h2 className="mt-4 text-xl font-semibold text-white">Ready to export?</h2>
                                <p className="mt-1 max-w-sm text-sm font-light text-white/80">
                                    We&apos;ll render your video and email you when it&apos;s done.
                                </p>
                            </div>
                        </div>

                        {/* Usage stat card */}
                        <div className="relative -mt-6 px-6">
                            <div className="flex items-center gap-3 rounded-2xl border border-[#12131714] bg-[#fcfcfd] p-4 shadow-sm">
                                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#7659fa1a] text-[#7659fa]">
                                    <i className="hgi-stroke hgi-download-04 text-lg" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Free exports used</p>
                                    <p className="text-sm font-semibold text-slate-800">
                                        {data?.current_export_count || 0} <span className="font-light text-slate-400">of {data?.export_limit}</span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 pt-4">
                            <div className="space-y-2 rounded-2xl border border-[#12131714] bg-[#fcfcfd] p-4">
                                <p className="text-sm font-light leading-relaxed text-slate-500">
                                    After your total limit is used, you&apos;ll be charged <strong className="font-semibold text-slate-700">{data?.export_credit} credits</strong> per export from your main credit balance.
                                </p>
                                <p className="flex items-start gap-2 text-xs font-light leading-relaxed text-slate-400">
                                    <i className="hgi-stroke hgi-mail-01 mt-0.5" />
                                    Once rendering completes, check your email for details or failure information.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 px-6 py-5">
                            <Button
                              variant="bordered"
                              className="h-11 rounded-xl border-1 border-[#00000014] bg-[#c8ceda4d] px-6 text-sm font-medium text-slate-700 hover:border-slate-700"
                              onPress={exportLimitUseModalOnOpenChange}>Cancel</Button>
                            <Button
                              isLoading={exportVideo.isPending}
                              className="h-11 rounded-xl bg-[#7659fa] px-6 text-sm font-medium text-white shadow-lg shadow-[#7659fa40] transition hover:opacity-95"
                              onPress={() => {
                                  exportVideo.mutate()
                              }}><i className="hgi-stroke hgi-download-04 text-[15px]" /> Confirm Export</Button>
                        </div>
                    </ModalBody>
                  )}
              </ModalContent>
          </Modal>


          {/* Timeline Modified — caution */}
          <Modal
            hideCloseButton
            isOpen={showWarning}
            onOpenChange={setShowWarning}
            size="lg"
            backdrop="blur"
            classNames={{
                base: "overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl",
                body: "p-0",
            }}>
              <ModalContent>
                  {(onClose) => (
                    <ModalBody>
                        <div className="relative overflow-hidden bg-gradient-to-br from-[#f59e0b] via-[#f5a623] to-[#fbbf24] px-8 pt-9 pb-12">
                            <div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
                            <div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
                            <div className="relative flex flex-col items-center text-center">
                                <div className="relative grid h-16 w-16 place-items-center">
                                    <span className="absolute inset-0 rounded-2xl bg-white/30 animate-ping" />
                                    <span className="relative grid h-16 w-16 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40">
                                        <i className="hgi-stroke hgi-clock-01 text-3xl text-white" />
                                    </span>
                                </div>
                                <h2 className="mt-4 text-xl font-semibold text-white">Timeline modified</h2>
                                <p className="mt-1 max-w-sm text-sm font-light text-white/85">
                                    Save your changes before you export.
                                </p>
                            </div>
                        </div>

                        <div className="px-6 pt-5">
                            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                                <i className="hgi-stroke hgi-alert-02 mt-0.5 text-lg text-amber-500" />
                                <p className="text-sm font-light leading-relaxed text-amber-700">
                                    The project timeline has been updated. Please review the changes. To continue, save
                                    the updates before exporting the process.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-center px-6 py-5">
                            <Button
                              className="h-11 rounded-xl bg-[#f59e0b] px-10 text-sm font-medium text-white shadow-lg shadow-[#f59e0b40] transition hover:opacity-95"
                              onPress={() => setShowWarning(false)}>
                                OK
                            </Button>
                        </div>
                    </ModalBody>
                  )}
              </ModalContent>
          </Modal>
      </>
    )
}